/**
 * The session lifecycle both consoles run, and the error taxonomy both face.
 *
 * React-free on purpose: the provider components stay in their own apps
 * (they expose genuinely different context values — the org's carries
 * impersonation state and an org id, the admin's carries a user) while the part
 * that is identical, and that drifted, lives here. It also means this is
 * testable without a DOM.
 */

/**
 * Refresh at 13 minutes against a 15-minute token.
 *
 * The two-minute margin is what stops a request being sent with a token that
 * expires in flight; refreshing at 15 would guarantee it occasionally.
 */
export const SESSION_REFRESH_INTERVAL_MS = 13 * 60 * 1000;

/**
 * Classify a failed session exchange into a type a UI can act on.
 *
 * Returning a type rather than a rendered string is the load-bearing part.
 * vista#372: the org console used to answer a 401 with
 * `setTimeout(() => location.href = …, 2000)`, intending to bounce the browser
 * through CF Access. When the subdomain is not covered by a CF Access
 * application policy, that navigation lands back on the same page, 401s again,
 * and loops — a flashing "redirecting…" panel with no way out. The fix was to
 * surface an actionable Sign In instead, which requires the caller to know
 * *which* failure it has.
 *
 * The admin console kept the redirect. It is removed with vista#671, and this
 * is the shared classification that replaces it.
 *
 * `auth_failed` means "not signed in" — a Sign In button helps. Every 403 means
 * "signed in, but not permitted" — a Sign In button does not help and offering
 * one invites the user to try the thing that cannot work.
 */
export function classifySessionError(err) {
  const code = err?.body?.error;
  const detail = err?.body?.message;

  if (err?.status === 403) {
    switch (code) {
      case 'admin_console_required':
        return { errorType: code, error: detail || 'Please use the Admin Console' };
      case 'org_mismatch':
        return {
          errorType: code,
          error: detail || 'You do not have access to this organization',
        };
      // #231: distinct from the catch-all so the user is told the actionable
      // thing (ask for a fresh invitation) rather than "not provisioned".
      case 'invitation_expired':
        return { errorType: code, error: detail || 'Your invitation has expired' };
      default:
        return {
          errorType: 'not_provisioned',
          error: detail || err.message || 'Account not provisioned',
        };
    }
  }

  if (err?.status === 401) {
    return { errorType: 'auth_failed', error: 'Sign in required' };
  }

  return { errorType: 'error', error: err?.message || 'Unable to connect to VISTA' };
}

/**
 * Drive a session: exchange, load the user, and keep the token fresh.
 *
 * @param api            the client from `createApiClient`
 * @param setVistaToken  its token setter
 * @param onUser         called with the `/me` payload on success
 * @param onError        called with `classifySessionError`'s result AND the raw
 *                       error, so a caller can log the cause. #679: a refresh
 *                       that fails must be diagnosable, not a silent logout
 * @param sessionBody    optional body for the initial exchange (the org console
 *                       sends `{ org_slug }` for subdomain routing; the admin
 *                       console sends nothing)
 *
 * Returns `{ init, refresh, cancelRefresh, dispose }`.
 *
 * `dispose` must be called on unmount — the refresh timer outlives the
 * component otherwise and fires against a torn-down tree. `cancelRefresh` is
 * the reversible form: the org console needs it because an impersonation token
 * has a 1-hour TTL and is NOT refreshable, so while impersonating there must be
 * no timer, and ending impersonation must be able to start one again.
 */
export function createSessionController({
  api,
  setVistaToken,
  onUser,
  onError,
  sessionBody,
  refreshIntervalMs = SESSION_REFRESH_INTERVAL_MS,
}) {
  let timer = null;
  let disposed = false;

  function clearTimer() {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function scheduleRefresh() {
    clearTimer();
    if (disposed) return;
    timer = setTimeout(() => {
      refresh();
    }, refreshIntervalMs);
  }

  /**
   * Establish the session from whatever the edge already proved about the user.
   *
   * The token is set BEFORE `/me` is called, because `/me` is authenticated by
   * it. Getting that order wrong yields a 401 from an exchange that just
   * succeeded, which is a confusing thing to debug.
   */
  async function exchange(path, body) {
    const session = await api.post(path, body);
    setVistaToken(session.data.vista_token);
    const me = await api.get('/me');
    return me.data;
  }

  async function init() {
    try {
      const user = await exchange('/auth/session', sessionBody);
      if (disposed) return;
      onUser(user);
      scheduleRefresh();
    } catch (err) {
      setVistaToken(null);
      if (disposed) return;
      onError(classifySessionError(err), err);
    }
  }

  async function refresh() {
    try {
      const user = await exchange('/auth/session/refresh');
      if (disposed) return;
      onUser(user);
      scheduleRefresh();
    } catch (err) {
      setVistaToken(null);
      if (disposed) return;
      // A failed *refresh* is not a failed sign-in: the user was authenticated
      // a moment ago and their token simply aged out, so it is reported as an
      // expired session rather than routed through the 401/403 taxonomy, which
      // would tell them they are not provisioned.
      onError({ errorType: 'session_expired', error: 'Session expired' }, err);
    }
  }

  /**
   * Cancel a pending refresh without retiring the controller.
   *
   * Distinct from `dispose` because it is reversible: `init()` afterwards
   * schedules a new timer.
   */
  function cancelRefresh() {
    clearTimer();
  }

  function dispose() {
    disposed = true;
    clearTimer();
  }

  return { init, refresh, cancelRefresh, dispose };
}
